import { useState } from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export function useWebAuthnAuth() {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const { toast } = useToast();

  const isWebAuthnSupported = () => {
    return window.PublicKeyCredential !== undefined && navigator.credentials !== undefined;
  };

  const authenticateWithPasskey = async (email: string) => {
    if (!isWebAuthnSupported()) {
      toast({
        title: 'Not Supported',
        description: 'WebAuthn is not supported in your browser',
        variant: 'destructive',
      });
      return { error: 'WebAuthn not supported' };
    }

    setIsAuthenticating(true);

    try {
      // Get authentication options from backend
      const { data: optionsData, error: optionsError } = await supabase.functions.invoke(
        'webauthn-authenticate',
        {
          body: { email, verify: false }
        }
      );

      if (optionsError) throw optionsError;
      if (!optionsData?.options) throw new Error('No authentication options received');

      const userId = optionsData.userId;

      // Start authentication with browser
      const credential = await startAuthentication(optionsData.options);

      // Verify authentication with backend
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke(
        'webauthn-authenticate',
        {
          body: {
            email,
            credential,
            verify: true,
            userId
          }
        }
      );

      if (verifyError) throw verifyError;
      if (!verifyData?.verified) throw new Error('Authentication failed');

      // Sign in the user with Supabase
      // Note: In a production app, the backend should create a session token
      // For now, we'll use a magic link approach or OTP
      const { error: signInError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
        }
      });

      if (signInError) throw signInError;

      toast({
        title: 'Authentication Successful',
        description: 'You have been signed in with your passkey',
      });

      return { error: null };
    } catch (error) {
      console.error('WebAuthn authentication error:', error);
      
      let errorMessage = 'Authentication failed';
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          errorMessage = 'Authentication was cancelled or timed out';
        } else if (error.name === 'InvalidStateError') {
          errorMessage = 'Invalid authenticator state';
        } else {
          errorMessage = error.message;
        }
      }

      toast({
        title: 'Authentication Failed',
        description: errorMessage,
        variant: 'destructive',
      });

      return { error: errorMessage };
    } finally {
      setIsAuthenticating(false);
    }
  };

  return {
    isWebAuthnSupported,
    authenticateWithPasskey,
    isAuthenticating
  };
}
